<template>
  <div class="table-responsive" style="min-height: 550px;">
    <table class="table  table-hover table-striped smart ">
      <thead>
        <tr class="bg-secondary">
          <th class="hide-md-only">ITEM</th>

          <th 
            v-for="col in visibleColumns" 
            :key="col.key"
            :class="typeof col.class === 'string' ? col.class : undefined"
          >
            {{ col.label }}
          </th>

          <th v-if="permittedActions.length">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in data" :key="rowKey ? rowKey(row, index) : index">
          <td data-label="ITEM" class="hide-md-only">
            {{ getGlobalIndex(index) }}
          </td>

          <td v-for="col in visibleColumns" :key="col.key" :data-label="col.label"
            :class="typeof col.class === 'function' ? col.class(row) : col.class">
            <span v-if="!col.render">{{ row[col.key] }}</span>
            <template v-else>
              <span v-if="col.render(row).type === 'badge'"
                :class="`badge-custom badge-${col.render(row).variant}-custom`">
                {{ col.render(row).text }}
              </span>
              <span v-else>{{ col.render(row) }}</span>
            </template>
          </td>

          <td v-if="permittedActions.length" class="text-right">
            <div class="btn-group">
              <template v-for="(action, i) in getVisibleActions(row)" :key="i">
                <!-- BOTÓN NORMAL -->
                <button
                  v-if="action.type !== 'dropdown'"
                  class="btn btn-sm btn-default"
                  :class="typeof action.class === 'string' ? action.class : undefined"
                  @click="handleActionClick(action, row)"
                  v-bind="getTooltipBinding(action, row)"
                >
                  <i :class="getIcon(action, row)"></i>
                </button>
                <!-- DROPDOWN COMO PARTE DEL BTN GROUP -->
                <div v-else class="btn-group">
                  <button
                    class="btn btn-sm btn-default dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    :class="typeof action.class === 'string' ? action.class : undefined"
                    v-bind="getTooltipBinding(action, row)"
                  >
                  </button>
                  <div class="dropdown-menu dropdown-menu-right">
                    <a v-for="(item, j) in action.items"
                      :key="j"
                      style="cursor: pointer;"
                      class="dropdown-item"
                      :class="typeof item.class === 'string' ? item.class : undefined"
                      @click="handleActionClick(item, row)"
                    >
                      <i v-if="item.icon" :class="item.icon" class="mr-2"></i>
                      {{ item.label }}
                    </a>
                  </div>
                </div>

              </template>

            </div>
          </td>
        </tr>

        <tr v-if="data.length === 0">
          <td :colspan="colspanValue">There is no data</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TableConfig } from './TypeTable'

  const props = defineProps<{
    data: any[]
    config: TableConfig<any>
    startIndex?: number
    rowKey?: (row: any, index: number) => string | number
  }>()

  // ─── filtrar por PERMISO (computed → se evalúa una vez, se cachea) ────
  const visibleColumns = computed(() =>
    (props.config.columns ?? []).filter(col =>
      col.permission ? col.permission() : true
    )
  )

  const permittedActions = computed(() =>
    (props.config.actions ?? [])
      .filter((action): action is NonNullable<typeof action> => {
        if (!action || typeof action !== 'object') return false
        return action.permission ? action.permission() : true
      })
      .map(action => {
        if (action.type !== 'dropdown') return action
        return {
          ...action,
          items: (action.items ?? []).filter((item: any) =>
            item.permission ? item.permission() : true
          )
        }
      })
  )

  // ─── filtrar por VISIBILIDAD de fila (función normal, depende de row) ─

  const getVisibleActions = (row: any) => {
    return permittedActions.value
      .map(action => {
        if (action.type === 'dropdown') {
          const visibleItems = (action.items ?? []).filter((item: any) =>
            item.show ? item.show(row) : true
          )
          if (visibleItems.length === 0) return null
          return { ...action, items: visibleItems }
        }

        const isVisible = action.show ? action.show(row) : true
        return isVisible ? action : null
      })
      .filter((action): action is NonNullable<typeof action> => action !== null)
  }

  const handleActionClick = (action: any, row: any) => {
    if (!action || action.type === 'dropdown') return
    if (typeof action.onClick === 'function') action.onClick(row)
    else console.warn('Acción inválida detectada:', action)
  }

  const getIcon = (action: any, row: any): string =>
    typeof action.icon === 'function' ? action.icon(row) : action.icon ?? ''

  const getTooltipBinding = (action: any, row: any) => {
    if (!action.tooltip) return {}
    const value = typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip
    return value ? { 'v-tooltip': value } : {}
  }

  const colspanValue = computed(() => {
    let cols = visibleColumns.value.length + 1
    if (permittedActions.value.length) cols++
    return cols
  })

  const getGlobalIndex = (index: number) => {
    const offset = props.startIndex ?? 0
    return offset - index
  }
</script>

<style>
.table.smart {
  font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  color: #333;
}

.table.smart tbody td {
  font-weight: 400;
}

.badge-custom {
  font-weight: 500;
  font-size: 0.75rem;
}

.btn-sm {
  font-size: 0.85rem;
}

.table-hover tbody tr:hover {
  background-color: #97a5d46c;
  box-shadow: inset 2px 0 0 #2C3E50;
}
</style>